import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { LOGO_SRC } from './design.js';
import { ENTORNO } from './entorno.js';

// ── Silenciar el aviso benigno de ResizeObserver ──────────────────────────
// "ResizeObserver loop completed with undelivered notifications" (y la variante
// "loop limit exceeded") NO es un error: el navegador lo emite cuando un observer
// provoca un cambio de layout en el mismo frame (p. ej. los textareas que crecen
// solos en el editor de conceptos). En producción es invisible; el overlay de
// desarrollo lo pintaba como error rojo (CRA; se conserva con Vite). Lo eliminamos en el origen
// difiriendo cada callback de ResizeObserver un frame (rAF): así no hay bucle en
// el mismo frame y el navegador no dispara el aviso.
if (typeof window !== 'undefined' && typeof window.ResizeObserver === 'function') {
  const NativeRO = window.ResizeObserver;
  window.ResizeObserver = class extends NativeRO {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}
// Red de seguridad: si algún bucle llegara igual, evita que un overlay de desarrollo lo
// trate como error en tiempo de ejecución (no afecta errores reales).
const _RO_BENIGN = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
window.addEventListener('error', (e) => {
  if (e && e.message && _RO_BENIGN.test(e.message)) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

// Inyectar favicon usando el mismo logo base64 del sistema
const faviconLink = document.createElement('link');
faviconLink.rel  = 'icon';
faviconLink.type = 'image/png';
faviconLink.href = LOGO_SRC;
document.head.appendChild(faviconLink);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);

// ── Service Worker (PWA: offline app-shell + notificaciones push) ──
// Solo en producción (en dev rompe el HMR de Vite).
//
// Ciclo de actualización (RD-05). El sw.js NO hace skipWaiting solo: un build
// nuevo se instala y precachea en segundo plano y queda en `reg.waiting` hasta
// que se cierren todas las ventanas. En una PWA instalada o en una pestaña que
// nunca se cierra eso significa quedarse en un build viejo indefinidamente.
// Aquí se detecta el build en espera y se le avisa a la UI con un evento de
// ventana; la UI decide cuándo aplicarlo (el técnico con una captura a medias
// no recibe un reload sorpresa).
//
// Contrato:
//   window 'gb:sw-actualizacion'  detail: { aplicar }
//       Se emite cuando hay un SW instalado en espera Y ya existe un controller
//       (no es la primera instalación). `aplicar()` manda SKIP_WAITING al SW en
//       espera; cuando toma control (controllerchange) la página se recarga UNA
//       sola vez. Sin `aplicar()` el controllerchange NO recarga nada.
//   window 'gb:sw-consulta'       sin detail
//       La UI lo dispara al montar para pedir que se re-emita el anuncio si ya
//       había un build en espera (el aviso pudo salir antes de que existiera el
//       chip: login, arranque).
const UPDATE_INTERVALO_MS = 30 * 60 * 1000;  // chequeo periódico
const UPDATE_THROTTLE_MS  = 5 * 60 * 1000;   // al volver a primer plano

function iniciarCicloActualizacionSW(reg) {
  let aplicarPedido = false;   // el usuario pulsó Actualizar
  let recargado = false;       // guard: controllerchange puede dispararse más de una vez
  let ultimoUpdate = 0;

  const aplicar = () => {
    aplicarPedido = true;
    const esperando = reg.waiting;
    if (esperando) {
      try { esperando.postMessage({ type: 'SKIP_WAITING' }); } catch (e) {}
      return;
    }
    // Ya no hay nadie en espera: el build nuevo tomó control por otra vía
    // (otra pestaña lo activó). Recargar para traer el shell vigente.
    if (!recargado) { recargado = true; window.location.reload(); }
  };

  const anunciar = () => {
    // Primera instalación: no hay controller, no hay "versión vieja" que reemplazar.
    if (!reg.waiting || !navigator.serviceWorker.controller) return;
    window.dispatchEvent(new CustomEvent('gb:sw-actualizacion', { detail: { aplicar } }));
  };

  // Un build nuevo empieza a instalarse → avisar cuando quede en espera.
  reg.addEventListener('updatefound', () => {
    const nuevo = reg.installing;
    if (!nuevo) return;
    nuevo.addEventListener('statechange', () => {
      if (nuevo.state === 'installed') anunciar();
    });
  });

  // El SW nuevo tomó control: recargar solo si fue a petición del usuario.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!aplicarPedido || recargado) return;
    recargado = true;
    window.location.reload();
  });

  // La UI pregunta al montar (el anuncio pudo salir antes de que existiera el chip).
  window.addEventListener('gb:sw-consulta', anunciar);

  const revisar = () => {
    ultimoUpdate = Date.now();
    try { reg.update().catch(() => {}); } catch (e) {}
  };

  // Ya había un build en espera de una sesión anterior (caso RD-05 clásico:
  // recargar no lo activa porque la pestaña vieja sigue siendo cliente).
  anunciar();
  revisar();
  setInterval(revisar, UPDATE_INTERVALO_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - ultimoUpdate < UPDATE_THROTTLE_MS) return;
    revisar();
  });
}

// En las rutas publicas (el QR de un reporte, el acuse, el portal de descargas)
// NO se registra: el SW precachea el build ENTERO al instalarse, y un cliente
// que escanea un QR se llevaba toda la app administrativa al cache de su
// telefono. No son datos, pero es la aplicacion completa regalada a alguien que
// jamas va a entrar. Si el dispositivo ya tenia el SW (personal interno), sigue
// controlando la pagina como siempre; solo no se instala desde aqui.
const RUTAS_PUBLICAS = ['/verificar/', '/firmante/', '/acuse/', '/reportes', '/portal-demo'];
const esRutaPublica = RUTAS_PUBLICAS.some(p => window.location.pathname.startsWith(p));
if ('serviceWorker' in navigator && ENTORNO.PRODUCCION && !esRutaPublica) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then(iniciarCicloActualizacionSW)
      .catch(() => {});
  });
}
